import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DataObjectIcon from '@mui/icons-material/DataObject';
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined';
import { useIntl } from 'react-intl';

import { Icon, type ProjectDetailHeaderMetaItem } from '@verantos-dev/shared-components';

// Condensed meta-row icon sizing — 16px across every item (Type · Domain(s) ·
// Owner · Last modified · Concepts) so the icons read as small captions.
const HEADER_ICON_SX = { fontSize: 16, color: 'text.secondary' } as const;

/**
 * The shared condensed meta row for a concept-group asset header —
 * Type · Domain(s) · Owner · Last modified · Concepts.
 * Built from primitives so BOTH the Draft review and the Ready detail view
 * render an identical row (only the values + the header's status chip differ).
 * Items with no value are omitted; ``conceptCount`` is optional (the Draft
 * header omits it — the count lives in its Concepts tab strip).
 */
export const useConceptGroupMetaItems = ({
  domains,
  ownerName,
  lastModifiedIso,
  conceptCount,
}: {
  domains: string[];
  ownerName?: string;
  lastModifiedIso?: string | null;
  conceptCount?: number | null;
}): ProjectDetailHeaderMetaItem[] => {
  const { formatMessage, formatDate, formatTime } = useIntl();
  const lastModified = lastModifiedIso
    ? `${formatDate(lastModifiedIso, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })} | ${formatTime(lastModifiedIso, { hour: 'numeric', minute: '2-digit' })}`
    : null;
  return [
    {
      key: 'type',
      icon: <DataObjectIcon sx={HEADER_ICON_SX} />,
      label: formatMessage({ id: 'epiChat.conceptGroupPreview.meta.typeLabel' }),
      value: formatMessage({ id: 'epiChat.conceptGroupPreview.meta.typeValue' }),
    },
    ...(domains.length > 0
      ? [
          {
            key: 'domains',
            icon: <MapsHomeWorkOutlinedIcon sx={HEADER_ICON_SX} />,
            label: formatMessage({ id: 'epiChat.conceptGroupPreview.meta.domainsLabel' }),
            value: domains.join(', '),
          },
        ]
      : []),
    ...(ownerName
      ? [
          {
            key: 'owner',
            icon: (
              <Icon
                id="cg-meta-owner-icon"
                icon="owner"
                width={16}
                height={16}
                sx={{ fill: (theme) => theme.palette.text.secondary }}
              />
            ),
            label: formatMessage({ id: 'epiChat.conceptGroupPreview.meta.ownerLabel' }),
            value: ownerName,
          },
        ]
      : []),
    ...(lastModified
      ? [
          {
            key: 'lastModified',
            icon: <AccessTimeOutlinedIcon sx={HEADER_ICON_SX} />,
            label: formatMessage({ id: 'epiChat.conceptGroupPreview.meta.lastModifiedLabel' }),
            value: lastModified,
          },
        ]
      : []),
    ...(typeof conceptCount === 'number'
      ? [
          {
            key: 'concepts',
            icon: <CheckCircleIcon sx={{ fontSize: 16 }} color="success" />,
            label: formatMessage({ id: 'epiChat.conceptGroupPreview.meta.conceptsLabel' }),
            value: conceptCount,
          },
        ]
      : []),
  ];
};
